import { apiClient } from './client';
import type { Vote, VoteStatistics, TallyResponse } from '../types';

export const votesApi = {
  /**
   * Get current user's vote for a subset
   */
  async getUserVote(subsetId: number): Promise<{ hasVoted: boolean; vote?: Vote }> {
    return apiClient.get<{ hasVoted: boolean; vote?: Vote }>(`/api/votes/${subsetId}`);
  },

  /**
   * Submit or update vote for a subset
   */
  async submitVote(
    subsetId: number,
    rankedSongIds: number[],
    specialCategoryVotes?: (number | null)[],
    isSubmitted: boolean = true
  ): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>(
      `/api/votes/${subsetId}`,
      { rankedSongIds, specialCategoryVotes, isSubmitted }
    );
  },

  /**
   * Save draft vote (not submitted/counted in tallies yet)
   */
  async saveDraft(
    subsetId: number,
    rankedSongIds: number[],
    specialCategoryVotes?: (number | null)[]
  ): Promise<{ success: boolean; message: string }> {
    return apiClient.post<{ success: boolean; message: string }>(
      `/api/votes/${subsetId}`,
      { rankedSongIds, specialCategoryVotes, isSubmitted: false }
    );
  },

  /**
   * Delete vote for a subset
   */
  async deleteVote(subsetId: number): Promise<{ success: boolean; message: string }> {
    return apiClient.delete<{ success: boolean; message: string }>(`/api/votes/${subsetId}`);
  },

  /**
   * Get vote tally (requires admin or live tally enabled)
   */
  async getTally(subsetId: number): Promise<TallyResponse> {
    return apiClient.get<TallyResponse>(`/api/votes/${subsetId}/tally`);
  },

  /**
   * Get admin statistics for a subset (admin only)
   */
  async getAdminStats(subsetId: number): Promise<VoteStatistics & {
    notVoted: Array<{ id: number; username: string }>;
    specialCategoryTallies: { [categoryIndex: number]: Array<{ songId: number; voteCount: number }> };
  }> {
    return apiClient.get(`/api/admin/stats/${subsetId}`);
  },

  /**
   * Export vote statistics as Excel file (admin only)
   */
  async exportStats(subsetId: number, subsetName: string): Promise<void> {
    const response = await fetch(`/api/admin/stats/${subsetId}/export`, {
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Export failed' }));
      throw new Error(error.error || 'Export failed');
    }

    // Create blob from response
    const blob = await response.blob();

    // Create download link and trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    // Filename format: Subset_Name_2026-01-02.xlsx
    const sanitizedName = subsetName
      .replace(/[^a-zA-Z0-9-_\s]/g, '')
      .replace(/\s+/g, '_');
    const date = new Date().toISOString().split('T')[0];
    a.download = `${sanitizedName}_${date}.xlsx`;

    document.body.appendChild(a);
    a.click();

    // Cleanup
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },
};
